import "../../css/GlobalStyles.css";
import { useTheme } from "../../hooks/useTheme";
import { useCallback, useContext, useEffect, useState } from "react";
import { MixpanelContext } from "../../context/AnalyticsService";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../Containers/PopupModalContainer";
import RectangleButton from "../Buttons/RectangleButton";
import { addAlpha, Colors } from "../../utils/colors";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import { useNavigate } from "../../hooks/useNavigate";
import { Icon } from "@iconify/react";
import HelpModal from "../Containers/HelpModal";
import MarkitLogoBlue from "../../assets/MarkitLogoBlue.png";
import TextHoverButton from "../Buttons/TextHoverButton";
import {
  getNotificationState,
  notificationActions,
} from "../../redux/slices/notificationSlice";
import { hasSubscription, isEssentialSubscription } from "@markit/common.utils";
import { PlanTypeV4, resolvePlanVariant } from "@markit/common.types";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import { FeatureItem } from "@markit/common.types/dist/subscription/interface";

type MarkitPlusModalProps = {
  alternateIsVisible?: boolean;
  alternateCloseOnPress?: () => void;
};

export const MarkitPlusModal = (props: MarkitPlusModalProps) => {
  const { alternateIsVisible, alternateCloseOnPress } = props;
  const { accountData } = useSelector(getAccountState).account;
  const { markitPlusModal } = useSelector(getNotificationState);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { theme } = useTheme();
  const mixpanel = useContext(MixpanelContext);
  const [helpModal, setHelpModal] = useState(false);

  const isVisible = Boolean(markitPlusModal || alternateIsVisible);

  useEffect(() => {
    if (isVisible) {
      mixpanel.track("Pressed Markit+ Subscription Prompt", {
        distinct_id: accountData.uid,
        from: "webapp",
      });
    }
  }, [accountData.uid, mixpanel, isVisible]);

  const target = alternateIsVisible
    ? PlanTypeV4.ESSENTIAL.name
    : (markitPlusModal ?? PlanTypeV4.ESSENTIAL.name);

  const config = resolvePlanVariant(
    isEssentialSubscription(accountData),
    target
  );
  const hasSub = hasSubscription(accountData);

  const onSubscriptionPress = useCallback(async () => {
    dispatch(notificationActions.setMarkitPlusModal(null));
    navigate("/subscription", {
      state: { changePlan: hasSub, targetPlan: target },
    });
  }, [dispatch, hasSub, navigate, target]);

  const closeOnPress = useCallback(() => {
    if (alternateCloseOnPress) {
      alternateCloseOnPress();
    } else {
      dispatch(notificationActions.setMarkitPlusModal(null));
    }
  }, [dispatch, alternateCloseOnPress]);

  const renderFeatureRow = useCallback(
    (feat: FeatureItem, isLast: boolean) => (
      <>
        <div
          key={feat.title}
          className="AlignedRow"
          style={{ gap: 10, paddingBlock: 10 }}
        >
          <div
            className="Centering"
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              backgroundColor: addAlpha(Colors.BLUE5, 0.1),
              border: `0.5px solid ${Colors.BLUE5}`,
              flexShrink: 0,
            }}
          >
            <Icon
              icon={`ion:${feat.icon}`}
              height={16}
              style={{ color: Colors.BLACK }}
            />
          </div>
          <div className="ColumnNormal" style={{ gap: 4 }}>
            <div className="bodyMedium">{feat.title}</div>
            <div className="smallBodyPlaceholder">{feat.desc}</div>
          </div>
          {feat.badge ? (
            <div
              className="bodyMinyMedium"
              style={{
                color: Colors.BLUE5,
                backgroundColor: addAlpha(Colors.BLUE5, 0.1),
                padding: 4,
                border: `0.5px solid ${Colors.BLUE5}`,
                borderRadius: 100,
                whiteSpace: "nowrap",
                alignSelf: "flex-start",
                flexShrink: 0,
              }}
            >
              {feat.badge}
            </div>
          ) : null}
        </div>
        {!isLast ? <HorizontalDivider /> : null}
      </>
    ),
    []
  );

  return (
    <>
      {isVisible && !helpModal ? (
        <PopupModalContainer
          headerComp={
            <div className="ColumnNormal" style={{ gap: 20 }}>
              <div className="AlignedRow" style={{ gap: 7 }}>
                <img
                  style={{ height: 22, width: "auto", objectFit: "contain" }}
                  src={MarkitLogoBlue}
                  alt="Markit logo"
                />
                <span className="bodyMedium">
                  Get Markit<span style={{ color: Colors.BLUE5 }}>+</span>
                </span>
              </div>
              <div className="ColumnNormal" style={{ gap: 4 }}>
                <span className="subheading">{config.title}</span>
                <span className="bodySubtext">{config.subtitle}</span>
              </div>
            </div>
          }
          valueComp={
            <div className="ColumnNormal" style={{ maxHeight: "70vh" }}>
              {config.valuePill ? (
                <>
                  <div
                    className="ColumnNormal"
                    style={{
                      gap: 7,
                      backgroundColor: addAlpha(Colors.BLUE5, 0.1),
                      border: `0.5px solid ${Colors.BLUE5}`,
                      borderRadius: 12,
                      padding: 14,
                    }}
                  >
                    <div className="bodyMedium">
                      {config.valuePill.boldText}
                    </div>
                    <div className="bodySmallRegular">
                      {config.valuePill.bodyText}
                    </div>
                  </div>
                  <HorizontalDivider altMargin={14} />
                </>
              ) : null}
              <div>
                {config.features.map((feat, i) =>
                  renderFeatureRow(feat, i === config.features.length - 1)
                )}
              </div>
            </div>
          }
          fixedBottomContent={
            <div
              className="ColumnNormal"
              style={{ paddingInline: 14, paddingTop: 14, gap: 7 }}
            >
              <div className="AlignedRowSpaced">
                <span className="heading">
                  {config.price}{" "}
                  <span className="smallBodyPlaceholder">/mo</span>
                </span>
                <span className="smallBodyPlaceholder">{config.priceFrom}</span>
              </div>
              <RectangleButton
                buttonLabel={config.ctaLabel}
                onPress={onSubscriptionPress}
                altColor={Colors.BLACK}
                altPaddingVert={14}
              />
              <div className="Centering">
                <TextHoverButton
                  text="Contact Support"
                  onPress={() => setHelpModal(true)}
                />
              </div>
            </div>
          }
          theme={theme}
          closeModal={closeOnPress}
          closeOnOutsidePress
          noExit
          modalContentWidth={PopupModalContainerWidth.NARROW}
        />
      ) : helpModal ? (
        <HelpModal showModal={helpModal} setShowModal={setHelpModal} />
      ) : null}
    </>
  );
};
